import { guestSessionOnMessage } from "../actions/onMessageActions/guestSessions";
import { noneSessionOnMessage } from "../actions/onMessageActions/noneSessions";
import { notarySessionOnMessage } from "../actions/onMessageActions/notarySessions";
import { sessionsOnMessage } from "../actions/onMessageActions/sessions";
import { userSessionOnMessage } from "../actions/onMessageActions/userSessions";
import { subUserSessionOnMessage } from "../actions/onMessageActions/subUserSessions";
import {
  updateSessionWsConnection,
  setSessionExpiredLogout,
} from "../actions/sessionsActions";
import { privateProviderSessionOnMessage } from "../actions/onMessageActions/privateProviderSessions";
import {
  stopPolling,
  catchUpMissedNotifications,
} from "../../services/notificationPolling";

// Registry to dedupe sockets per sessionType across middleware instances
const socketRegistry = new Map(); // sessionType -> WebSocket | null
const getSocket = (sessionType) => socketRegistry.get(sessionType) || null;
const setSocket = (sessionType, socket) =>
  socketRegistry.set(sessionType, socket);

const onMessageActions = {
  guestSession: guestSessionOnMessage,
  notarySession: notarySessionOnMessage,
  userSession: userSessionOnMessage,
  privateProviderSession: privateProviderSessionOnMessage,
  subUserSession: subUserSessionOnMessage,
  sessions: sessionsOnMessage,
  noneSession: noneSessionOnMessage,
};

const createWebSocketMiddleware = (sessionType) => {
  // use registry instead of local var to avoid duplicate connections
  let reconnectTimer = null;
  let reconnectAttempts = 0;
  let forcedLogoutTriggered = false;
  const maxReconnectAttempts = 6;
  let connectionTimeout = 15000; // 10 seconds timeout for connection
  const reconnectDelay = 2000; // 5 seconds delay for reconnection
  let connectionTimer = null;
  let pollingRecipientId = null; // Store recipient ID for polling

  const getSessionToken = (state, sessionType) => {
    switch (sessionType) {
      case "userSession":
        return state.sessions?.userSession.token;
      case "notarySession":
        return state.sessions?.notarySession.token;
      case "guestSession":
        return state.sessions?.guestSession.token;
      case "subUserSession":
        return state.sessions?.subUserSession.token;
      case "privateProviderSession":
        return state.sessions?.privateProviderSession.token;
      default:
        return null; // or appropriate error handling
    }
  };

  // Get recipient ID for polling (guest_id, user_id, notary_id)
  // const getRecipientIdForPolling = (state) => {
  //   // For guest sessions, get guest_id from guestSession state
  //   if (sessionType === "guestSession") {
  //     // Try to get from guestSession.registerData or manageData
  //     const guestData = state.guestSession;
  //     const guestId =
  //       guestData?.registerData?.guest_id ||
  //       guestData?.manageData?.guest_id ||
  //       sessionStorage.getItem("guest_id");
  //     return guestId;
  //   }

  //   // For user sessions
  //   if (sessionType === "userSession") {
  //     const userData = state.user;
  //     return userData?._id || userData?.id || sessionStorage.getItem("user_id");
  //   }

  //   // For subUser sessions
  //   if (sessionType === "subUserSession") {
  //     const subUserData = state.subUser;
  //     return (
  //       subUserData?._id || subUserData?.id || sessionStorage.getItem("user_id")
  //     );
  //   }

  //   // For notary sessions
  //   if (sessionType === "notarySession") {
  //     const notaryData = state.notary;
  //     return (
  //       notaryData?.notary_id ||
  //       notaryData?._id ||
  //       sessionStorage.getItem("notary_id")
  //     );
  //   }

  //   return null;
  // };

  const handleWebSocketMessage = async (dispatch, getState, receivedData) => {
    const sessionToken = getSessionToken(getState(), sessionType);
    const actionCreator = onMessageActions[receivedData.type];

    if (actionCreator) {
      if (
        receivedData.type === sessionType ||
        receivedData.type === "sessions" ||
        receivedData.type === "noneSession"
      ) {
        if (
          receivedData?.event === "heartbeat" &&
          receivedData.type === sessionType
        ) {
          if (receivedData.token === sessionToken) {
            const exp = sessionStorage.getItem("exp");
            const extended = sessionStorage.getItem("extended");

            if (receivedData.data.will_expire_soon && !extended) {
              sessionStorage.setItem("willExpire", "true");
              const event = new Event("expireUpdate");
              window.dispatchEvent(event);
            } else if (!receivedData.data.will_expire_soon) {
              sessionStorage.removeItem("willExpire");
              sessionStorage.removeItem("extended");
            }

            if (receivedData.data.session_expired && !extended) {
              sessionStorage.setItem("expiredSession", "true");
              sessionStorage.removeItem("__nocCreator__r_rid");
              sessionStorage.removeItem("exp");
              sessionStorage.removeItem("willExpire");
              sessionStorage.removeItem("extended");

              await triggerForcedLogout(dispatch, getState);
              return;
            }

            // Echo back the heartbeat message
            const response = JSON.stringify({
              type: receivedData.type, // Echo back the received type
              token: receivedData.token, // Echo back the token for verification
              extended: extended,
              exp: exp,
              timestamp: new Date().toISOString(),
            });

            try {
              const socket = getSocket(sessionType);
              if (socket && socket.readyState === WebSocket.OPEN) {
                socket.send(response);
              }
            } catch (e) {
              console.error("Failed to send heartbeat response:", e);
            }
          } else {
            console.error(
              `Received token does not match stored token. Received: ${receivedData.token}, Expected: ${sessionToken}`,
            );
          }
        } else {
          const nextAction = actionCreator(receivedData);

          // Some onMessage handlers intentionally ignore unknown events.
          // Never dispatch undefined/null to Redux.
          if (!nextAction) {
            return;
          }

          await dispatch(nextAction);
        }
      } else {
        // Handle unexpected session types if needed
      }
    } else {
      console.error("No action creator found for type:", receivedData.type);
    }
  };

  const triggerForcedLogout = async (dispatch, getState) => {
    if (forcedLogoutTriggered || getState().sessions?.sessionForcedLogout) {
      return;
    }

    forcedLogoutTriggered = true;
    stopPolling(sessionType);
    pollingRecipientId = null;
    clearTimeout(connectionTimer);
    clearTimeout(reconnectTimer);

    await dispatch({
      type: `${sessionType.toUpperCase()}_WEBSOCKET_DISCONNECT`,
    });
    dispatch(setSessionExpiredLogout());
  };

  const connectWebSocket = (dispatch, getState, url, token) => {
    const sessions = getState().sessions;
    const { sessionForcedLogout } = sessions;

    // Prevent duplicate connections under StrictMode double-dispatch
    const existing = getSocket(sessionType);
    if (
      existing &&
      (existing.readyState === WebSocket.OPEN ||
        existing.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    if (existing !== null && existing.readyState !== WebSocket.CLOSED) {
      try {
        existing.close();
      } catch {}
    }
    setSocket(sessionType, new WebSocket(url));
    const socket = getSocket(sessionType);

    connectionTimer = setTimeout(() => {
      if (socket && socket.readyState === WebSocket.CONNECTING) {
        console.error(
          "WebSocket connection timed out. Attempting to reconnect...",
        );
        socket.close();
        reconnectWebSocket(dispatch, getState, url, token);
      }
    }, connectionTimeout);

    socket.onopen = () => {
      clearInterval(reconnectTimer);
      clearTimeout(connectionTimer);
      reconnectAttempts = 0;

      // Stop polling when WebSocket reconnects
      stopPolling(sessionType);

      // Catch up on any missed notifications while disconnected
      if (pollingRecipientId) {
        catchUpMissedNotifications(
          sessionType,
          pollingRecipientId,
          (notification) => {
            handleWebSocketMessage(dispatch, getState, notification);
          },
        );
      }

      const state = getState();
      const sessions = state?.sessions;
      const { currentSession } = sessions; // e.g., "userSession"

      const currentConnection = sessions?.[currentSession];

      if (
        currentConnection?.isValidated &&
        currentConnection?.token &&
        currentConnection?.wsConnected &&
        currentConnection?.token
      ) {
        return;
      } else {
        dispatch(
          updateSessionWsConnection({
            sessionType: sessionType,
            wsConnected: true,
          }),
        );
      }
    };

    socket.onmessage = (event) => {
      const receivedData = JSON.parse(event.data);
      handleWebSocketMessage(dispatch, getState, receivedData);
    };

    socket.onerror = (error) => {
      if (!sessionForcedLogout) {
        console.error(`${sessionType} WebSocket error:`, error);
      }
    };

    socket.onclose = (event) => {
      clearTimeout(connectionTimer);
      setSocket(sessionType, null);

      const currentSessions = getState().sessions;

      if (forcedLogoutTriggered || currentSessions?.sessionForcedLogout) {
        return;
      }

      if (reconnectAttempts < maxReconnectAttempts) {
        setTimeout(() => {
          const latestSessions = getState().sessions;
          if (!forcedLogoutTriggered && !latestSessions?.sessionForcedLogout) {
            reconnectWebSocket(dispatch, getState, url, token);
          }
        }, 2000); // Add a small delay
      } else {
        triggerForcedLogout(dispatch, getState);
      }
    };
  };

  const reconnectWebSocket = (dispatch, getState, url, token) => {
    const sessions = getState().sessions;
    const { sessionForcedLogout } = sessions;

    if (sessionForcedLogout) {
      return;
    }

    if (forcedLogoutTriggered) {
      return;
    }

    reconnectAttempts++;
    if (reconnectAttempts <= maxReconnectAttempts) {
      const delay = Math.min(reconnectDelay * 1.5 ** reconnectAttempts, 10000); // Faster reconnect
      reconnectTimer = setTimeout(
        () => connectWebSocket(dispatch, getState, url, token),
        delay,
      );
    } else {
      triggerForcedLogout(dispatch, getState);
    }
  };

  return (store) => (next) => (action) => {
    const { dispatch, getState } = store;
    // Extract the session type from the action type
    const actionPrefix = action?.type?.split("_")[0].toLowerCase();

    // Only handle actions that match this middleware's sessionType
    if (actionPrefix !== sessionType.toLowerCase()) {
      return next(action);
    }

    // Process only the actions for this sessionType
    switch (action.type) {
      case `${sessionType.toUpperCase()}_WEBSOCKET_CONNECT`:
        connectWebSocket(
          dispatch,
          getState,
          action.payload.url,
          action.payload.token,
        );
        break;
      case `${sessionType.toUpperCase()}_WEBSOCKET_DISCONNECT`:
        {
          const socket = getSocket(sessionType);
          if (socket !== null) {
            try {
              socket.onclose = null;
              socket.close();
            } catch {}
            setSocket(sessionType, null);
          }
        }
        // Stop polling on explicit disconnect
        stopPolling(sessionType);
        pollingRecipientId = null;
        dispatch(
          updateSessionWsConnection({
            sessionType: sessionType,
            wsConnected: false,
          }),
        );
        clearInterval(reconnectTimer);
        clearTimeout(connectionTimer);
        reconnectAttempts = 0;
        forcedLogoutTriggered = false;
        break;
      case `${sessionType.toUpperCase()}_WEBSOCKET_SEND_MESSAGE`:
        {
          const socket = getSocket(sessionType);
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(action.payload.message));
          }
        }
        break;
      default:
        return next(action);
    }
  };
};

export default createWebSocketMiddleware;
